import { useEffect, useState } from "react";

import { Destination } from "Constant/destination";

/**
 * Selected destination type, the generic credential fields (pixel id, token,
 * TikTok app id, OpenAI keys) with their errors, and the create-pixel switch.
 */
const useDestinationForm = ({ language, setSaveDisabled }) => {
    const [destinationCode, setDestinationCode] = useState();
    const [typeError, setTypeError] = useState(null);
    const [credentials, setCredentials] = useState({});
    const [pixelIdError, setPixelIdError] = useState("");
    const [accessTokenError, setAccessTokenError] = useState("");
    const [appIdError, setAppIdError] = useState("");
    const [conversionsKeyError, setConversionsKeyError] = useState("");
    const [advertiserKeyError, setAdvertiserKeyError] = useState("");
    const [createPixelStatus, setCreatePixelStatus] = useState(true);
    const [googleTagId, setGoogleTagId] = useState(null);

    const selectDestinationCode = (code) => {
        setDestinationCode(code);
        if (typeError) setTypeError(null);
    };

    const reset = () => setDestinationCode(undefined);

    const resetCredentials = () => setCredentials({});

    /** Clears type, credentials, every field error and the pixel toggles. */
    const resetAll = () => {
        setDestinationCode(undefined);
        setTypeError(null);
        setCredentials({});
        setPixelIdError("");
        setAccessTokenError("");
        setAppIdError("");
        setConversionsKeyError("");
        setAdvertiserKeyError("");
        setCreatePixelStatus(true);
        setGoogleTagId(null);
    };

    const setCredentialField = (field, value) => {
        setCredentials({
            ...credentials,
            [field]: value,
        });
    };

    /** Returns `false` (and shows the error) when no type is selected. */
    const validateDestinationCode = () => {
        if (destinationCode) return true;
        setTypeError(
            language?.destination_modal?.select_a_destination ??
                "Select a destination",
        );
        return false;
    };

    const clearFieldErrors = () => {
        setPixelIdError("");
        setAccessTokenError("");
        setAppIdError("");
    };

    const handlePixelChange = (event) => {
        setCredentialField("pixel_id", event.target.value);
        setPixelIdError("");
        if (destinationCode === Destination.ttapp) return;
        setSaveDisabled(event.target.value === "");
    };

    const handleTokenChange = (event) => {
        setSaveDisabled(true);
        setCredentialField("token", event.target.value);
        setAccessTokenError("");
        if (destinationCode === Destination.ttapp) return;
        if (event.target.value !== "" && credentials?.pixel_id !== "") {
            setSaveDisabled(false);
        }
    };

    const handleAppIdChange = (event) => {
        setCredentialField("app_id", event.target.value);
        setAppIdError("");
    };

    const handleOpenAiKeyChange = (field, clearError) => (event) => {
        setCredentialField(field, event.target.value);
        clearError("");
    };

    const handlePixelStatusChange = (event) => {
        setCreatePixelStatus(event.target.checked);
    };

    useEffect(() => {
        if (destinationCode === Destination.ttapp) {
            setSaveDisabled(
                !credentials?.pixel_id ||
                    !credentials?.token ||
                    !credentials?.app_id,
            );
        }
    }, [destinationCode, credentials, setSaveDisabled]);

    return {
        destinationCode,
        selectDestinationCode,
        typeError,
        validateDestinationCode,
        reset,
        resetAll,
        credentials,
        resetCredentials,
        pixelIdError,
        setPixelIdError,
        accessTokenError,
        setAccessTokenError,
        appIdError,
        setAppIdError,
        conversionsKeyError,
        setConversionsKeyError,
        advertiserKeyError,
        setAdvertiserKeyError,
        clearFieldErrors,
        createPixelStatus,
        handlePixelChange,
        handleTokenChange,
        handleAppIdChange,
        handleOpenAiKeyChange,
        handlePixelStatusChange,
        googleTagId,
        setGoogleTagId,
    };
};

export default useDestinationForm;
